import React from "react";
import { Form } from "react-bootstrap";
import FilterNumber from "../../../jsx/components/FilterNumberDropDown/FilterNumber";
import SearchFilter from "../../../jsx/components/SearchFilter/SearchFilter";
import RefreshButton from "../../../jsx/components/RefreshButton/RefreshButton";
import DownloadExcelButton from "../../../jsx/components/DownloadExcelButton.jsx/DownloadExcelButton";
import { EXCLUDED_EXCEL_KEYS } from "./registrationFileUtils";

const getObjectKeys = (obj, prefix = "") => {
  if (!obj || typeof obj !== "object") return [];
  return Object.keys(obj).flatMap((key) => {
    const value = obj[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      return getObjectKeys(value, path);
    }
    return [path];
  });
};

const getDynamicHeaders = (data = []) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  const keySet = new Set();
  data.forEach((row) => {
    if (row && typeof row === "object") {
      getObjectKeys(row).forEach((k) => {
        if (!EXCLUDED_EXCEL_KEYS.has(k)) {
          keySet.add(k);
        }
      });
    }
  });

  const keys = Array.from(keySet);
  const orderedKeys = keys.includes("serial")
    ? ["serial", ...keys.filter((k) => k !== "serial")]
    : keys;

  return orderedKeys.map((key) => ({
    label:
      key === "serial"
        ? "SN"
        : key
            .replace(/\./g, " ")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
    key,
  }));
};

const RegistrationFilterBar = ({
  searchValue,
  setSearchValue,
  perPage,
  onPerPageChange,
  filters,
  onFilterChange,
  sportsDisciplines = [],
  trainingCentres = [],
  districts = [],
  onRefresh,
  excelData = [],
}) => {
  const dynamicHeaders = React.useMemo(
    () => getDynamicHeaders(excelData),
    [excelData]
  );

  return (
    <div className="px-3 pt-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div>
            <FilterNumber value={perPage} onChange={onPerPageChange} />
          </div>

          <div style={{ width: "140px" }}>
            <Form.Select
              value={filters.discipline}
              onChange={(e) => onFilterChange("discipline", e.target.value)}
              style={{ width: "100%", padding: "9px 12px", textOverflow: "ellipsis" }}
            >
              <option value="">All Sports</option>
              {sportsDisciplines.map((item) => (
                <option key={item.id} value={item.discipline_name}>
                  {item.discipline_name}
                </option>
              ))}
            </Form.Select>
          </div>

          <div style={{ width: "230px" }}>
            <Form.Select
              value={filters.preferred_centre}
              onChange={(e) => onFilterChange("preferred_centre", e.target.value)}
              style={{ width: "100%", padding: "9px 12px", textOverflow: "ellipsis" }}
            >
              <option value="">All Preferred Centres</option>
              {trainingCentres.map((centre) => {
                const locationPart =
                  centre.sport_training_centre_address || centre.district;
                const formattedLocation = locationPart
                  ? locationPart.startsWith("(") && locationPart.endsWith(")")
                    ? locationPart
                    : `(${locationPart})`
                  : "";
                const fullLocation =
                  centre.sport_training_centre_name && formattedLocation
                    ? `${centre.sport_training_centre_name} ${formattedLocation}`
                    : centre.sport_training_centre_name || formattedLocation;
                return (
                  <option key={centre.id} value={centre.id}>
                    {fullLocation}
                  </option>
                );
              })}
            </Form.Select>
          </div>

          <div style={{ width: "150px" }}>
            <Form.Select
              value={filters.district_id}
              onChange={(e) => onFilterChange("district_id", e.target.value)}
              style={{ width: "100%", padding: "9px 12px", textOverflow: "ellipsis" }}
            >
              <option value="">All Districts</option>
              {districts.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Form.Select>
          </div>

          <div style={{ width: "105px" }}>
            <Form.Select
              value={filters.age}
              onChange={(e) => onFilterChange("age", e.target.value)}
              style={{ width: "100%", padding: "9px 12px" }}
            >
              <option value="">All Ages</option>
              {[16, 17, 18, 19, 20, 21, 22].map((age) => (
                <option key={age} value={age}>
                  {age}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-nowrap ms-auto">
          <div>
            <DownloadExcelButton
              data={excelData}
              headers={dynamicHeaders}
              btnName="Download Excel"
              fileName="Registration.xlsx"
            />
          </div>

          <div style={{ width: "190px" }}>
            <SearchFilter
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>

          {onRefresh && (
            <div>
              <RefreshButton refreshCallback={onRefresh} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationFilterBar;
